import React, { useMemo } from 'react';
import { Chart as GoogleChart } from 'react-google-charts';
import { useResolvedTheme } from 'providers/ThemeModeProvider';
import { resolveCssVars } from 'UI/theme/cssVariables';
import { ThemeModes } from 'UI/theme/themeMode';

import { DARK_CHART_DEFAULTS } from './ThemedChart.constants';

const isPlainObject = value => value !== null && typeof value === 'object' && !Array.isArray(value);

const mergeDefaults = (defaults, options = {}) =>
  Object.entries(defaults).reduce(
    (merged, [key, value]) => {
      const current = options[key];
      if (current !== undefined && current !== null && !isPlainObject(current)) {
        return { ...merged, [key]: current };
      }
      return {
        ...merged,
        [key]: isPlainObject(value) ? mergeDefaults(value, current ?? {}) : current ?? value
      };
    },
    { ...options }
  );

/**
 * Drop-in replacement for `react-google-charts` `Chart`. Google Charts cannot parse CSS variables,
 * so theme tokens (`THEME.palette.*`, `colors.*`) in `options` are resolved to concrete colors of
 * the active mode. In dark mode it also applies dark surface and text defaults; explicit options win.
 */
export const Chart = ({ options, ...rest }) => {
  const { resolvedMode, cssVars } = useResolvedTheme();

  const themedOptions = useMemo(() => {
    const withDefaults =
      resolvedMode === ThemeModes.Dark
        ? mergeDefaults(resolveCssVars(DARK_CHART_DEFAULTS, cssVars), options)
        : options;
    return resolveCssVars(withDefaults, cssVars);
  }, [cssVars, options, resolvedMode]);

  return <GoogleChart options={themedOptions} {...rest} />;
};

export default Chart;
